import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Download, Image as ImageIcon, Loader2, Plus, Save, Search, Trash2, Upload } from "lucide-react";
import { api, formatErr, resolveMediaUrl } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const emptyProduct = {
  product_name: "",
  slug: "",
  category_id: "",
  category: "",
  sub_category: "",
  brand: "",
  model_number: "",
  product_code: "",
  sku: "",
  barcode: "",
  short_description: "",
  full_description: "",
  features: [],
  specifications: [{ key: "", value: "" }],
  warranty: "",
  main_image: "",
  gallery_images: [],
  product_video: "",
  whatsapp_number: "",
  seo_title: "",
  seo_description: "",
  meta_keywords: "",
  image_alt_text: "",
  active: true,
  featured: false,
  new_arrival: false,
  display_order: 0,
  brochure_access: "public",
  brochure_template: "single",
};

const emptyCategory = { name: "", slug: "", description: "", image: "", display_order: 0, active: true };
const emptyBranding = {
  rp_logo: "",
  address: "",
  website: "",
  email: "",
  phone: "",
  watermark: "",
  footer: "",
  primary_color: "#07142f",
  accent_color: "#f97316",
};

function normalizeTextPayload(row) {
  return Object.fromEntries(Object.entries(row || {}).map(([key, value]) => [key, value == null ? "" : String(value)]));
}

function previewUrl(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^https?:\/\//i.test(text) || text.startsWith("data:") || text.startsWith("blob:")) return text;
  if (text.startsWith("/api/") || text.includes("/api/media/serve/")) return resolveMediaUrl(text);
  return text.startsWith("/") ? text : resolveMediaUrl(text);
}

function isVideoUrl(value) {
  const text = String(value || "").split("?")[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov|m4v)$/i.test(text);
}

export default function AdminECatalogue() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [websiteCategories, setWebsiteCategories] = useState([]);
  const [logs, setLogs] = useState([]);
  const [branding, setBranding] = useState(emptyBranding);
  const [form, setForm] = useState(emptyProduct);
  const [editing, setEditing] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [categoryEditing, setCategoryEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [websiteTargetCategory, setWebsiteTargetCategory] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const loadProducts = useCallback(async () => {
    const { data } = await api.get("/admin/e-catalogue/products", { params: { q: query, limit: 300 } });
    setProducts(data.items || []);
  }, [query]);
  const loadCategories = async () => {
    const { data } = await api.get("/admin/e-catalogue/categories");
    setCategories(data || []);
  };
  const loadWebsiteCategories = async () => {
    const { data } = await api.get("/admin/website");
    const names = [...new Set((data.productCategories || []).map((cat) => cat.name).filter(Boolean))];
    setWebsiteCategories(names);
  };
  const loadBranding = async () => {
    const { data } = await api.get("/admin/e-catalogue/branding");
    setBranding({ ...emptyBranding, ...(data || {}) });
  };
  const loadLogs = async () => {
    const { data } = await api.get("/admin/e-catalogue/download-logs");
    setLogs(data || []);
  };

  useEffect(() => {
    loadCategories().catch(() => {});
    loadWebsiteCategories().catch(() => {});
    loadBranding().catch(() => {});
    loadLogs().catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts().catch((e) => toast.error(formatErr(e)));
    }, 180);
    return () => clearTimeout(timer);
  }, [loadProducts]);

  const categoryName = useMemo(() => Object.fromEntries(categories.map((cat) => [cat.id, cat.name])), [categories]);

  const resetForm = () => {
    setEditing(null);
    setForm(emptyProduct);
  };

  const editProduct = (product) => {
    setEditing(product);
    setForm({
      ...emptyProduct,
      ...product,
      features: product.features || [],
      specifications: product.specifications?.length ? product.specifications : [{ key: "", value: "" }],
      gallery_images: product.gallery_images || [],
      brochure_access: product.brochure_access === "private" ? "private" : "public",
    });
    setTab("products");
  };

  const saveProduct = async () => {
    if (!form.product_name.trim()) {
      toast.error("Product name is required");
      return;
    }
    setBusy(true);
    const payload = {
      ...form,
      features: (form.features || []).filter(Boolean),
      specifications: (form.specifications || []).filter((row) => row.key),
      gallery_images: (form.gallery_images || []).filter(Boolean),
      category: categoryName[form.category_id] || form.category,
      brochure_access: form.brochure_access === "private" ? "private" : "public",
    };
    try {
      if (editing) await api.put(`/admin/e-catalogue/products/${editing.id}`, payload);
      else await api.post("/admin/e-catalogue/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      resetForm();
      await loadProducts();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Delete ${product.product_name}?`)) return;
    await api.delete(`/admin/e-catalogue/products/${product.id}`);
    toast.success("Product deleted");
    await loadProducts();
  };

  const duplicateProduct = async (product) => {
    await api.post(`/admin/e-catalogue/products/${product.id}/duplicate`);
    toast.success("Product duplicated as inactive copy");
    await loadProducts();
  };

  const copyToWebsite = async (product) => {
    try {
      await api.post(`/admin/e-catalogue/products/${product.id}/copy-to-website`, null, { params: { target_category: websiteTargetCategory } });
      toast.success("Product copied to Website Products");
      await loadWebsiteCategories();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const copySelectedToWebsite = async () => {
    if (!selected.length) {
      toast.error("Select products first");
      return;
    }
    setBusy(true);
    try {
      await Promise.all(selected.map((id) => api.post(`/admin/e-catalogue/products/${id}/copy-to-website`, null, { params: { target_category: websiteTargetCategory } })));
      toast.success(`${selected.length} product(s) copied to Website Products`);
      setSelected([]);
      await loadWebsiteCategories();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const bulk = async (action, extra = {}) => {
    if (!selected.length) {
      toast.error("Select products first");
      return;
    }
    if (action === "delete" && !window.confirm(`Delete ${selected.length} products?`)) return;
    await api.post("/admin/e-catalogue/products/bulk", { ids: selected, action, ...extra });
    setSelected([]);
    toast.success("Bulk action completed");
    await loadProducts();
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    if (categoryEditing) await api.put(`/admin/e-catalogue/categories/${categoryEditing.id}`, categoryForm);
    else await api.post("/admin/e-catalogue/categories", categoryForm);
    toast.success(categoryEditing ? "Category updated" : "Category created");
    setCategoryForm(emptyCategory);
    setCategoryEditing(null);
    await loadCategories();
    await loadProducts();
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete ${cat.name}? Products will be uncategorized.`)) return;
    await api.delete(`/admin/e-catalogue/categories/${cat.id}`);
    toast.success("Category deleted");
    await loadCategories();
    await loadProducts();
  };

  const upload = async (file, onUrl, folder = "catalogue") => {
    if (!file) return;
    try {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("folder", folder);
      const { data } = await api.post("/admin/e-catalogue/upload", payload);
      onUrl(data.public_url || `/api/media/serve/${data.id}`);
      toast.success("File uploaded");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const saveBranding = async () => {
    try {
      const { data } = await api.put("/admin/e-catalogue/branding", normalizeTextPayload(branding));
      setBranding({ ...emptyBranding, ...(data || {}) });
      toast.success("Company branding saved");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div data-testid="admin-e-catalogue-page">
      <PageHeader overline="Admin / E-Catalogue Management" title="E-Catalogue." subtitle="Manage products, images, specifications, QR codes and auto-generated brochures.">
        <Button onClick={resetForm} className="rounded-sm bg-[#111827] text-white"><Plus className="mr-2 h-4 w-4" /> New Product</Button>
      </PageHeader>

      <div className="mb-5 flex flex-wrap gap-2 border border-slate-200 bg-slate-100 p-2">
        {["products", "categories", "branding", "logs"].map((item) => (
          <button key={item} onClick={() => setTab(item)} className={`px-4 py-2 text-sm font-black capitalize ${tab === item ? "bg-white text-slate-950 shadow-sm" : "text-slate-600"}`}>{item}</button>
        ))}
      </div>

      {tab === "products" && (
        <div className="grid gap-5 xl:grid-cols-[1fr_440px]">
          <section className="min-w-0">
            <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative max-w-lg flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search product, model, SKU, barcode..." className="rounded-sm pl-9" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-sm" onClick={() => bulk("status", { active: true })}>Enable</Button>
                <Button variant="outline" className="rounded-sm" onClick={() => bulk("status", { active: false })}>Disable</Button>
                <select className="rounded-sm border border-slate-200 px-3 py-2 text-sm font-bold" onChange={(e) => e.target.value && bulk("category", { category_id: e.target.value })} value="">
                  <option value="">Bulk category</option>
                  {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
                <select className="rounded-sm border border-blue-200 px-3 py-2 text-sm font-bold text-blue-700" value={websiteTargetCategory} onChange={(e) => setWebsiteTargetCategory(e.target.value)}>
                  <option value="">Copy to matching website category</option>
                  {websiteCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
                <Button variant="outline" className="rounded-sm border-blue-200 text-blue-700" disabled={busy} onClick={copySelectedToWebsite}><Copy className="mr-2 h-4 w-4" />Copy Selected to Website</Button>
                <Button variant="outline" className="rounded-sm text-red-600" onClick={() => bulk("delete")}><Trash2 className="mr-2 h-4 w-4" />Bulk Delete</Button>
              </div>
            </div>

            <div className="overflow-hidden border border-slate-200 bg-white">
              <div className="max-h-[720px] overflow-auto">
                <table className="w-full min-w-[900px] text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-[0.16em] text-slate-500">
                    <tr>
                      <th className="p-3"><input type="checkbox" checked={selected.length > 0 && selected.length === products.length} onChange={(e) => setSelected(e.target.checked ? products.map((p) => p.id) : [])} /></th>
                      <th className="p-3">Product</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Model / SKU</th>
                      <th className="p-3">Badges</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} className="border-t border-slate-100">
                        <td className="p-3"><input type="checkbox" checked={selected.includes(product.id)} onChange={(e) => setSelected((rows) => e.target.checked ? [...rows, product.id] : rows.filter((id) => id !== product.id))} /></td>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <img src={resolveMediaUrl(product.main_image) || "/logo.png"} alt="" className="h-14 w-14 border border-slate-100 object-contain" />
                            <div>
                              <div className="font-black text-slate-950">{product.product_name}</div>
                              <div className="text-xs text-slate-500">/{product.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">{product.category || "-"}</td>
                        <td className="p-3 font-mono text-xs">{product.model_number || "-"}<br />{product.sku || product.product_code || ""}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {product.featured && <span className="bg-blue-50 px-2 py-1 text-xs font-black text-blue-700">Featured</span>}
                            {product.new_arrival && <span className="bg-orange-50 px-2 py-1 text-xs font-black text-orange-700">New</span>}
                          </div>
                        </td>
                        <td className="p-3"><span className={product.active ? "font-black text-green-700" : "font-black text-slate-400"}>{product.active ? "Active" : "Disabled"}</span></td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => editProduct(product)}><Save className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => duplicateProduct(product)}><Copy className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" title="Copy to Website Products" onClick={() => copyToWebsite(product)}><Copy className="h-4 w-4 text-blue-600" /></Button>
                            <a href={`/e-catalogue/${product.slug}`} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center"><Download className="h-4 w-4" /></a>
                            <Button variant="ghost" size="icon" onClick={() => deleteProduct(product)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-slate-500">No catalogue products found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <ProductEditor
            form={form}
            setForm={setForm}
            editing={editing}
            categories={categories}
            upload={upload}
            saveProduct={saveProduct}
            busy={busy}
            resetForm={resetForm}
          />
        </div>
      )}

      {tab === "categories" && (
        <div className="grid gap-5 lg:grid-cols-[420px_1fr]">
          <section className="border border-slate-200 bg-white p-5">
            <h2 className="text-xl font-black">{categoryEditing ? "Edit Category" : "Add Category"}</h2>
            <div className="mt-4 grid gap-3">
              <Field label="Category name" value={categoryForm.name} onChange={(v) => setCategoryForm((f) => ({ ...f, name: v }))} />
              <Field label="Slug" value={categoryForm.slug} onChange={(v) => setCategoryForm((f) => ({ ...f, slug: v }))} />
              <Field label="Display order" type="number" value={categoryForm.display_order} onChange={(v) => setCategoryForm((f) => ({ ...f, display_order: Number(v) }))} />
              <TextField label="Description" value={categoryForm.description} onChange={(v) => setCategoryForm((f) => ({ ...f, description: v }))} />
              <UploadField label="Category image" value={categoryForm.image} accept="image/*" onUpload={(file) => upload(file, (url) => setCategoryForm((f) => ({ ...f, image: url })), "catalogue-categories")} onChange={(v) => setCategoryForm((f) => ({ ...f, image: v }))} />
              <Check label="Active" checked={categoryForm.active} onChange={(v) => setCategoryForm((f) => ({ ...f, active: v }))} />
              <Button onClick={saveCategory} className="rounded-sm bg-slate-950 text-white"><Save className="mr-2 h-4 w-4" />Save Category</Button>
            </div>
          </section>
          <section className="grid gap-3">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center justify-between gap-3 border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <img src={resolveMediaUrl(cat.image) || "/logo.png"} alt="" className="h-14 w-14 border border-slate-100 object-contain" />
                  <div><div className="font-black">{cat.name}</div><div className="text-xs text-slate-500">/{cat.slug} · Order {cat.display_order}</div></div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-sm" onClick={() => { setCategoryEditing(cat); setCategoryForm({ ...emptyCategory, ...cat }); }}>Edit</Button>
                  <Button variant="outline" className="rounded-sm text-red-600" onClick={() => deleteCategory(cat)}>Delete</Button>
                </div>
              </div>
            ))}
          </section>
        </div>
      )}

      {tab === "branding" && (
        <section className="grid gap-4 border border-slate-200 bg-white p-5 md:grid-cols-2">
          <UploadField label="RP Logo" value={branding.rp_logo} accept="image/*" onUpload={(file) => upload(file, (url) => setBranding((b) => ({ ...b, rp_logo: url })), "catalogue-branding")} onChange={(v) => setBranding((b) => ({ ...b, rp_logo: v }))} />
          <Field label="Website" value={branding.website} onChange={(v) => setBranding((b) => ({ ...b, website: v }))} />
          <Field label="Email" value={branding.email} onChange={(v) => setBranding((b) => ({ ...b, email: v }))} />
          <Field label="Phone" value={branding.phone} onChange={(v) => setBranding((b) => ({ ...b, phone: v }))} />
          <Field label="Primary brand color" value={branding.primary_color} onChange={(v) => setBranding((b) => ({ ...b, primary_color: v }))} />
          <Field label="Accent brand color" value={branding.accent_color} onChange={(v) => setBranding((b) => ({ ...b, accent_color: v }))} />
          <Field label="Watermark" value={branding.watermark} onChange={(v) => setBranding((b) => ({ ...b, watermark: v }))} />
          <Field label="Footer" value={branding.footer} onChange={(v) => setBranding((b) => ({ ...b, footer: v }))} />
          <TextField label="Address" value={branding.address} onChange={(v) => setBranding((b) => ({ ...b, address: v }))} wide />
          <div className="md:col-span-2"><Button onClick={saveBranding} className="rounded-sm bg-slate-950 text-white"><Save className="mr-2 h-4 w-4" />Save Branding</Button></div>
        </section>
      )}

      {tab === "logs" && (
        <section className="overflow-hidden border border-slate-200 bg-white">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.16em] text-slate-500"><tr><th className="p-3">Product</th><th className="p-3">Dealer</th><th className="p-3">Format</th><th className="p-3">IP Address</th><th className="p-3">Date</th></tr></thead>
            <tbody>{logs.map((log) => <tr key={log.id} className="border-t border-slate-100"><td className="p-3 font-black">{log.product_name}</td><td className="p-3">{log.dealer || log.user_role}</td><td className="p-3 uppercase">{log.format}</td><td className="p-3 font-mono text-xs">{log.ip_address}</td><td className="p-3">{new Date(log.downloaded_at).toLocaleString()}</td></tr>)}</tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function ProductEditor({ form, setForm, editing, categories, upload, saveProduct, busy, resetForm }) {
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <aside className="h-max border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div><h2 className="text-xl font-black">{editing ? "Edit Product" : "Add Product"}</h2><p className="text-xs text-slate-500">{editing ? editing.slug : "Create a catalogue item"}</p></div>
        {editing && <Button variant="outline" className="rounded-sm" onClick={resetForm}>New</Button>}
      </div>
      <div className="grid gap-3">
        <Field label="Product Name" value={form.product_name} onChange={(v) => set("product_name", v)} />
        <Field label="Slug" value={form.slug} onChange={(v) => set("slug", v)} />
        <label className="grid gap-1 text-sm font-bold">Category<select value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className="rounded-sm border border-slate-200 px-3 py-2"><option value="">Uncategorized</option>{categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}</select></label>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Sub Category" value={form.sub_category} onChange={(v) => set("sub_category", v)} />
          <Field label="Brand" value={form.brand} onChange={(v) => set("brand", v)} />
          <Field label="Model Number" value={form.model_number} onChange={(v) => set("model_number", v)} />
          <Field label="Product Code" value={form.product_code} onChange={(v) => set("product_code", v)} />
          <Field label="SKU" value={form.sku} onChange={(v) => set("sku", v)} />
          <Field label="Barcode" value={form.barcode} onChange={(v) => set("barcode", v)} />
          <Field label="Display Order" type="number" value={form.display_order} onChange={(v) => set("display_order", Number(v))} />
          <Field label="WhatsApp Number" value={form.whatsapp_number} onChange={(v) => set("whatsapp_number", v)} />
        </div>
        <TextField label="Short Description" value={form.short_description} onChange={(v) => set("short_description", v)} />
        <TextField label="Full Description" value={form.full_description} onChange={(v) => set("full_description", v)} />
        <LinesField label="Features" rows={form.features} onChange={(rows) => set("features", rows)} />
        <SpecField rows={form.specifications} onChange={(rows) => set("specifications", rows)} />
        <Field label="Warranty" value={form.warranty} onChange={(v) => set("warranty", v)} />
        <UploadField label="Main Image" value={form.main_image} accept="image/*" onUpload={(file) => upload(file, (url) => set("main_image", url), "catalogue-products")} onChange={(v) => set("main_image", v)} />
        <LinesField label="Gallery Images" rows={form.gallery_images} onChange={(rows) => set("gallery_images", rows)} upload={(file, done) => upload(file, done, "catalogue-gallery")} />
        <UploadField label="Product Video" value={form.product_video} accept="video/*" mediaType="video" onUpload={(file) => upload(file, (url) => set("product_video", url), "catalogue-video")} onChange={(v) => set("product_video", v)} />
        <div className="rounded-sm border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
          <div className="font-black">PDF brochure is generated automatically.</div>
          <p className="mt-1 leading-6">No separate brochure upload is needed. The public PDF uses this product's title, images, model, SKU, brand, warranty, description, specifications and live QR code.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-sm font-bold">Generated Brochure Access<select value={form.brochure_access} onChange={(e) => set("brochure_access", e.target.value)} className="rounded-sm border border-slate-200 px-3 py-2"><option value="public">Public Download</option><option value="private">Private (No Download)</option></select></label>
          <label className="grid gap-1 text-sm font-bold">Generated Brochure Layout<select value={form.brochure_template} onChange={(e) => set("brochure_template", e.target.value)} className="rounded-sm border border-slate-200 px-3 py-2"><option value="single">Single Page</option><option value="two_page">Two Page</option><option value="catalogue">Multi Page Catalogue</option></select></label>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Check label="Active" checked={form.active} onChange={(v) => set("active", v)} />
          <Check label="Featured" checked={form.featured} onChange={(v) => set("featured", v)} />
          <Check label="New Arrival" checked={form.new_arrival} onChange={(v) => set("new_arrival", v)} />
        </div>
        <Field label="SEO Title" value={form.seo_title} onChange={(v) => set("seo_title", v)} />
        <TextField label="SEO Description" value={form.seo_description} onChange={(v) => set("seo_description", v)} />
        <Field label="Meta Keywords" value={form.meta_keywords} onChange={(v) => set("meta_keywords", v)} />
        <Field label="Image Alt Text" value={form.image_alt_text} onChange={(v) => set("image_alt_text", v)} />
        <Button onClick={saveProduct} disabled={busy} className="rounded-sm bg-slate-950 text-white">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{editing ? "Save Product" : "Create Product"}</Button>
      </div>
    </aside>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return <label className="grid gap-1 text-sm font-bold">{label}<Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm" /></label>;
}

function TextField({ label, value, onChange, wide }) {
  return <label className={`grid gap-1 text-sm font-bold ${wide ? "md:col-span-2" : ""}`}>{label}<Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="min-h-24 rounded-sm" /></label>;
}

function Check({ label, checked, onChange }) {
  return <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} /> {label}</label>;
}

function UploadField({ label, value, onChange, onUpload, accept = "", mediaType = "image" }) {
  const src = previewUrl(value);
  const showVideo = mediaType === "video" || isVideoUrl(value);
  return (
    <label className="grid gap-1 text-sm font-bold">
      {label}
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm" />
        <label className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-slate-200"><Upload className="h-4 w-4" /><input type="file" accept={accept} className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} /></label>
      </div>
      {value && showVideo ? (
        <video src={src} controls muted playsInline className="mt-2 h-32 w-full border border-slate-100 bg-black object-contain" />
      ) : value ? (
        <img src={src} alt="" className="mt-2 h-20 w-28 border border-slate-100 object-contain" />
      ) : null}
    </label>
  );
}

function LinesField({ label, rows = [], onChange, upload }) {
  const values = rows?.length ? rows : [""];
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between"><span className="text-sm font-bold">{label}</span><Button type="button" variant="outline" className="h-8 rounded-sm" onClick={() => onChange([...values, ""])}><Plus className="h-3 w-3" /></Button></div>
      {values.map((row, index) => (
        <div key={index} className="flex gap-2">
          <Input value={row || ""} onChange={(e) => onChange(values.map((item, i) => (i === index ? e.target.value : item)))} className="rounded-sm" />
          {upload && <label className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-slate-200"><ImageIcon className="h-4 w-4" /><input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0], (url) => onChange(values.map((item, i) => (i === index ? url : item))))} /></label>}
          <Button variant="ghost" size="icon" onClick={() => onChange(values.filter((_, i) => i !== index))}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}

function SpecField({ rows = [], onChange }) {
  const values = rows?.length ? rows : [{ key: "", value: "" }];
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between"><span className="text-sm font-bold">Unlimited Specification Fields</span><Button variant="outline" className="h-8 rounded-sm" onClick={() => onChange([...values, { key: "", value: "" }])}><Plus className="h-3 w-3" /></Button></div>
      {values.map((row, index) => (
        <div key={index} className="grid grid-cols-[1fr_1fr_40px] gap-2">
          <Input placeholder="Key" value={row.key || ""} onChange={(e) => onChange(values.map((item, i) => (i === index ? { ...item, key: e.target.value } : item)))} className="rounded-sm" />
          <Input placeholder="Value" value={row.value || ""} onChange={(e) => onChange(values.map((item, i) => (i === index ? { ...item, value: e.target.value } : item)))} className="rounded-sm" />
          <Button variant="ghost" size="icon" onClick={() => onChange(values.filter((_, i) => i !== index))}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}
